import { useCallback, useMemo, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { TiptapDoc } from '@odo/shared-ui';
import {
    useCreateProduct,
    useProductTaxonomy,
    useSaveProductVersions,
    useUpdateProduct
} from '../../hooks/useBilling';
import { useToast } from '../../providers/ToastProvider';
import { uiErrorMessage } from '../../utils/apiError';
import { CategoryChip } from './CategoryChip';
import { DiscountRuleModal } from './DiscountRuleModal';
import { DiscountRulesSection } from './products/DiscountRulesSection';
import { MerchandisingSection } from './products/MerchandisingSection';
import { Field, SelectField } from './products/ProductFormField';
import { VendorPicker } from './products/OrderMethodPicker';
import {
    buildCreateVersionMethodChecks,
    buildUpdateVersionMethodChecks,
    checkVendorAssignmentOutcome,
    checkVersionMethodOutcome
} from './products/productOutcome';
import { VersionEditCard } from './products/VersionEditCard';
import {
    buildUpdateBody,
    marketingCopyDocForSave,
    toMerchandisingState,
    buildVersionChanges,
    emptyVersionRow,
    toFormState,
    toNullable,
    toVersionBody,
    toVersionRows,
    validateProductForm,
    webstoreReadinessGaps,
    WEBSTORE_CATEGORY,
    type FormState,
    type VersionFormRow
} from './products/productFormState';
import { useResolvedCopyDoc } from './products/useResolvedCopyDoc';
import { deleteProductImage, uploadProductImage } from '../../services/productImages';
import type { ProductDiscountRule, ProductRow, ProductVersion } from '../../services/billing';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Create + edit modal for a base product and its versions: create posts the
// whole form in one request, edit diffs it. Discount rules save on their own.

interface ProductModalProps {
    product: ProductRow | null; // null = create
    onClose: () => void;
}

export function ProductModal({ product, onClose }: ProductModalProps) {
    const isEdit = product !== null;
    const { showToast } = useToast();
    const { mutateAsync: create, isPending: isCreating } = useCreateProduct();
    const { mutateAsync: update, isPending: isUpdating } = useUpdateProduct();
    const { mutateAsync: saveVersions, isPending: isSavingVersions } = useSaveProductVersions();
    const [form, setForm] = useState<FormState>(() => toFormState(product));
    const [versionRows, setVersionRows] = useState<VersionFormRow[]>(() =>
        product ? toVersionRows(product.versions) : [emptyVersionRow('Standard')]
    );
    // undefined = closed, null = create a new rule, otherwise edit that rule.
    const [ruleModal, setRuleModal] = useState<ProductDiscountRule | null | undefined>(undefined);
    const [merchandising, setMerchandising] = useState(() => toMerchandisingState(product));
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const {
        categories,
        isLoading: isTaxonomyLoading,
        isError: isTaxonomyError
    } = useProductTaxonomy();

    const isPending = isCreating || isUpdating || isSavingVersions || isUploadingImage;

    // Suspended while the nested discount-rule editor is up - two enabled
    // instances both fire on one keypress and would discard the product edit too.
    useEscapeKey(onClose, !isPending && ruleModal === undefined);
    // Without the lists, an editable dropdown could only offer "clear it" - so
    // it stays locked on whatever the product already has.
    const isTaxonomyLocked = isTaxonomyLoading || isTaxonomyError;
    const taxonomyHint = isTaxonomyError
        ? 'Choices could not be loaded; existing values are kept.'
        : undefined;

    const setCopyDoc = useCallback((doc: TiptapDoc) => {
        setMerchandising((prev) => ({ ...prev, marketingCopyDoc: doc }));
    }, []);

    // Lives here, not in MerchandisingSection: that unmounts when the category
    // leaves Webstore, and a fresh guard would re-resolve over the user's edits.
    useResolvedCopyDoc(product?.id ?? null, product?.marketingCopyDoc ?? null, setCopyDoc);

    // Dropping the image drops storefront readiness, so the sale flag goes with it.
    const removeStoredImage = () => {
        setMerchandising((prev) => ({ ...prev, imageFileId: null, sellableInWebstore: false }));
    };

    const setSellable = (sellable: boolean) => {
        setMerchandising((prev) => ({ ...prev, sellableInWebstore: sellable }));
    };

    // Merchandising and the version webstore fields only apply to supplies; a
    // UI affordance only, since the server enforces sale eligibility.
    const isWebstoreCategory = form.category === WEBSTORE_CATEGORY;

    const readinessGaps = useMemo(
        () =>
            webstoreReadinessGaps(
                Boolean(imageFile || merchandising.imageFileId),
                merchandising.marketingCopyDoc,
                versionRows
            ),
        [imageFile, merchandising.imageFileId, merchandising.marketingCopyDoc, versionRows]
    );

    const setField = (field: keyof FormState) => (value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // Leaving the webstore category hides the sale switch, so drop the flag with it - a
    // product cannot stay in the storefront with no way to switch it off.
    const setCategory = (value: string) => {
        setField('category')(value);
        if (value !== WEBSTORE_CATEGORY) setSellable(false);
    };

    const patchVersion = (key: number, patch: Partial<VersionFormRow>) => {
        setVersionRows((prev) =>
            prev.map((row) => (row._key === key ? { ...row, ...patch } : row))
        );
    };

    const addVersion = () => setVersionRows((prev) => [...prev, emptyVersionRow()]);

    const removeVersion = (key: number) => {
        setVersionRows((prev) => (prev.length > 1 ? prev.filter((row) => row._key !== key) : prev));
    };

    // The stored object outlives the row that pointed at it, so a dropped or
    // replaced image is deleted once the row no longer references it.
    const discardReplacedImage = (nextImageFileId: string | null) => {
        const previousId = product?.imageFileId ?? null;
        if (!previousId || previousId === nextImageFileId) return;
        void deleteProductImage(previousId).catch(() => {
            /* the save already landed - a leaked object must not fail it */
        });
    };

    // Upload first, then write the row: a failed row write only orphans a GCS
    // object, never the reverse.
    const resolveImageFileId = async (): Promise<string | null> => {
        if (!imageFile) return merchandising.imageFileId;
        setIsUploadingImage(true);
        try {
            return (await uploadProductImage(imageFile)).fileId;
        } finally {
            setIsUploadingImage(false);
        }
    };

    const submitCreate = async (imageFileId: string | null) => {
        const vendorId = toNullable(form.vendorId);
        const created = await create({
            name: form.name.trim(),
            description: toNullable(form.description),
            category: toNullable(form.category),
            subCategory: toNullable(form.subCategory),
            vendorId,
            imageFileId,
            marketingCopyDoc: marketingCopyDocForSave(null, merchandising.marketingCopyDoc),
            sellableInWebstore: merchandising.sellableInWebstore,
            versions: versionRows.map(toVersionBody)
        });
        const drift = [
            checkVendorAssignmentOutcome({ vendorId }, created),
            checkVersionMethodOutcome(
                buildCreateVersionMethodChecks(
                    versionRows.map((row) => ({
                        label: row.label.trim(),
                        orderMethodId: toNullable(row.orderMethodId)
                    })),
                    created.versions
                )
            )
        ].filter((message): message is string => message !== null);
        showToast(drift.join(' ') || 'Product created', drift.length > 0 ? 'error' : 'success');
    };

    const submitEdit = async (edited: ProductRow, imageFileId: string | null) => {
        const body = buildUpdateBody(edited, form, { ...merchandising, imageFileId });
        const versionChanges = buildVersionChanges(edited.versions, versionRows);
        const hasVersionChanges =
            versionChanges.create.length > 0 ||
            versionChanges.update.length > 0 ||
            versionChanges.delete.length > 0;

        // Closing silently on no changes reads as a save that did nothing.
        if (Object.keys(body).length === 0 && !hasVersionChanges) {
            showToast('No changes to save', 'success');
            return;
        }

        let vendorDrift: string | null = null;
        let savedVersions: ProductVersion[] = [];
        const writeProduct = async () => {
            if (Object.keys(body).length === 0) return;
            const saved = await update({ id: edited.id, body });
            vendorDrift = checkVendorAssignmentOutcome(body, saved);
        };
        const writeVersions = async () => {
            if (hasVersionChanges)
                savedVersions = await saveVersions({
                    productId: edited.id,
                    changes: versionChanges
                });
        };

        // Both gates read the database: versions must land before a patch that
        // turns the storefront on, and after one that turns it off (it un-gates them).
        if (body.sellableInWebstore === false) {
            await writeProduct();
            await writeVersions();
        } else {
            await writeVersions();
            await writeProduct();
        }
        discardReplacedImage(imageFileId);

        const drift = [
            vendorDrift,
            checkVersionMethodOutcome(buildUpdateVersionMethodChecks(versionChanges, savedVersions))
        ].filter((message): message is string => message !== null);
        if (drift.length > 0) {
            showToast(drift.join(' '), 'error');
            return;
        }
        // A vendor saved without a method on every version leaves those versions
        // unorderable, so a plain "updated" would misreport that as success.
        const versionsWithoutMethod = versionRows.filter(
            (row) => row.orderMethodId.trim().length === 0
        );
        const unassigned = form.vendorId.trim().length > 0 && versionsWithoutMethod.length > 0;
        showToast(
            unassigned
                ? `Product updated. ${versionsWithoutMethod.length} of ${versionRows.length} version(s) have no ordering method, so no purchase order will be raised for them`
                : 'Product updated',
            unassigned ? 'error' : 'success'
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationError = validateProductForm(form, versionRows, {
            sellableInWebstore: merchandising.sellableInWebstore,
            readinessGaps
        });
        if (validationError) {
            showToast(validationError, 'error');
            return;
        }

        try {
            const imageFileId = await resolveImageFileId();
            if (product) await submitEdit(product, imageFileId);
            else await submitCreate(imageFileId);
            onClose();
        } catch (error) {
            showToast(
                uiErrorMessage(
                    error,
                    isEdit ? 'Failed to update product' : 'Failed to create product'
                ),
                'error'
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center p-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[760px] max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                    <div className="flex items-center gap-2.5">
                        <h2 className="text-lg font-bold text-gray-800">
                            {isEdit ? 'Edit product' : 'New product'}
                        </h2>
                        {isEdit && <CategoryChip category={product.category} />}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form
                    onSubmit={(e) => {
                        void handleSubmit(e);
                    }}
                    className="flex flex-col min-h-0 flex-1"
                >
                    <div className="p-6 flex flex-col gap-5 overflow-y-auto flex-1">
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Name"
                                value={form.name}
                                onChange={setField('name')}
                                required
                            />
                            <VendorPicker
                                value={form.vendorId}
                                disabled={isPending}
                                onChange={(vendorId) => setForm((prev) => ({ ...prev, vendorId }))}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <SelectField
                                label="Category"
                                value={form.category}
                                onChange={setCategory}
                                options={categories}
                                emptyLabel="No category"
                                disabled={isTaxonomyLocked}
                                hint={taxonomyHint}
                            />
                            <Field
                                label="Sub-category"
                                value={form.subCategory}
                                onChange={setField('subCategory')}
                            />
                        </div>
                        {isEdit && (
                            <div className="grid grid-cols-2 gap-4">
                                <label className="flex flex-col gap-1.5">
                                    <span className="text-[13px] font-semibold text-gray-700">
                                        Expiry date
                                    </span>
                                    <input
                                        type="date"
                                        value={form.expiresAt}
                                        onChange={(e) => setField('expiresAt')(e.target.value)}
                                        className="appearance-none block w-full px-3.5 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm text-gray-800 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-100"
                                    />
                                    <span className="text-[11px] text-gray-400">
                                        Past this date the product is hidden from charge and pricing
                                        pickers. Leave empty to keep it active.
                                    </span>
                                </label>
                            </div>
                        )}
                        <label className="flex flex-col gap-1.5">
                            <span className="text-[13px] font-semibold text-gray-700">
                                Description
                            </span>
                            <textarea
                                value={form.description}
                                onChange={(e) => setField('description')(e.target.value)}
                                rows={3}
                                placeholder="Shown as the default line-item description on charges"
                                className="appearance-none block w-full px-3.5 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-sm text-gray-800 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-100 resize-none"
                            />
                        </label>

                        {isWebstoreCategory && (
                            <MerchandisingSection
                                imageFile={imageFile}
                                storedImageFileId={merchandising.imageFileId}
                                onPickImage={setImageFile}
                                onRemoveStoredImage={removeStoredImage}
                                onRejectImage={(reason) => showToast(reason, 'error')}
                                copyDoc={merchandising.marketingCopyDoc}
                                onCopyDocChange={setCopyDoc}
                                sellableInWebstore={merchandising.sellableInWebstore}
                                onSellableChange={setSellable}
                                readinessGaps={readinessGaps}
                                disabled={isPending}
                            />
                        )}

                        {/* Versions - each carries an open key/value selector set */}
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-baseline justify-between">
                                <span className="text-[13px] font-semibold text-gray-700">
                                    Versions <span className="text-gray-400">*</span>
                                </span>
                                <span className="text-[11px] text-gray-400 text-right">
                                    Selectors are key/value pairs; more specific sets win. No
                                    selectors matches every practice.
                                    {isWebstoreCategory &&
                                        ' Pack size is the units-per-box multiplier, not a quantity.'}
                                </span>
                            </div>
                            {versionRows.map((row) => (
                                <VersionEditCard
                                    key={row._key}
                                    row={row}
                                    canRemove={versionRows.length > 1}
                                    showWebstoreFields={isWebstoreCategory}
                                    vendorId={form.vendorId}
                                    disabled={isPending}
                                    onChange={(patch) => patchVersion(row._key, patch)}
                                    onRemove={() => removeVersion(row._key)}
                                />
                            ))}
                            <button
                                type="button"
                                onClick={addVersion}
                                className="h-9 rounded-lg border border-dashed border-gray-300 text-[13px] font-semibold text-gray-500 hover:bg-gray-50 flex items-center justify-center gap-2"
                            >
                                <PlusIcon className="h-4 w-4" strokeWidth={2} />
                                Add version
                            </button>
                        </div>

                        {isEdit && (
                            <DiscountRulesSection
                                product={product}
                                onOpenRule={(rule) => setRuleModal(rule)}
                            />
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end items-center gap-2.5 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-sm font-semibold text-gray-500 px-4 py-2 hover:text-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isPending}
                            className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold shadow hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}
                        </button>
                    </div>
                </form>
            </div>

            {isEdit && ruleModal !== undefined && (
                <DiscountRuleModal
                    product={product}
                    rule={ruleModal}
                    onClose={() => setRuleModal(undefined)}
                />
            )}
        </div>
    );
}
